import React from 'react';
import { Phone, MessageCircle, MapPin, Clock, ArrowUp } from 'lucide-react';
import { companyInfo, seoKeywords } from '../data/api';

const Footer = () => {
  const handleCall = () => {
    window.location.href = `tel:${companyInfo.phoneClean}`;
  };

  const handleWhatsApp = () => {
    const message = encodeURIComponent(companyInfo.whatsappMessage);
    window.open(`https://wa.me/${companyInfo.phoneClean.replace('+', '')}?text=${message}`, '_blank');
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="bg-zinc-950 border-t border-zinc-800">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          {/* Company Info */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-amber-600 rounded-xl flex items-center justify-center shadow-lg shadow-amber-500/30">
                <span className="text-zinc-900 font-black text-xl">T</span>
              </div>
              <div>
                <h3 className="text-2xl font-bold text-white">{companyInfo.name}</h3>
                <p className="text-amber-400 text-sm">Denizli Taksi</p>
              </div>
            </div>
            <p className="text-zinc-400 mb-6 leading-relaxed">
              Denizli'nin güvenilir taksi hizmeti. 7/24 şehir içi, havalimanı transferi 
              ve şehirlerarası yolculuk hizmetleri.
            </p>
            <div className="flex gap-3">
              <button
                onClick={handleCall}
                className="w-10 h-10 bg-amber-500 hover:bg-amber-400 rounded-lg flex items-center justify-center text-zinc-900 transition-all duration-300 hover:scale-110"
                aria-label="Telefon"
              >
                <Phone className="w-5 h-5" />
              </button>
              <button
                onClick={handleWhatsApp}
                className="w-10 h-10 bg-green-600 hover:bg-green-500 rounded-lg flex items-center justify-center text-white transition-all duration-300 hover:scale-110"
                aria-label="WhatsApp"
              >
                <MessageCircle className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-semibold text-lg mb-4">Hızlı Linkler</h4>
            <nav className="space-y-3">
              {[
                { label: 'Ana Sayfa', id: 'hero' },
                { label: 'Hizmetler', id: 'services' },
                { label: 'Fiyatlar', id: 'prices' },
                { label: 'Hakkımızda', id: 'about' },
                { label: 'İletişim', id: 'contact' }
              ].map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className="block text-zinc-400 hover:text-amber-400 transition-colors duration-300"
                >
                  {item.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-white font-semibold text-lg mb-4">Hizmetlerimiz</h4>
            <ul className="space-y-3 text-zinc-400">
              <li className="hover:text-amber-400 transition-colors cursor-pointer">Şehir İçi Taksi</li>
              <li className="hover:text-amber-400 transition-colors cursor-pointer">Havalimanı Transfer</li>
              <li className="hover:text-amber-400 transition-colors cursor-pointer">Şehirlerarası Yolculuk</li>
              <li className="hover:text-amber-400 transition-colors cursor-pointer">VIP Transfer</li>
              <li className="hover:text-amber-400 transition-colors cursor-pointer">Pamukkale Turları</li>
              <li className="hover:text-amber-400 transition-colors cursor-pointer">Hastane Transfer</li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-white font-semibold text-lg mb-4">İletişim</h4>
            <div className="space-y-4">
              <div className="flex items-center gap-3 text-zinc-400">
                <Phone className="w-5 h-5 text-amber-400 flex-shrink-0" />
                <span>{companyInfo.phone}</span>
              </div>
              <div className="flex items-center gap-3 text-zinc-400">
                <MapPin className="w-5 h-5 text-amber-400 flex-shrink-0" />
                <span>Denizli, Türkiye</span>
              </div>
              <div className="flex items-center gap-3 text-zinc-400">
                <Clock className="w-5 h-5 text-amber-400 flex-shrink-0" />
                <span>7/24 Hizmet</span>
              </div>
              <div className="mt-4 p-3 bg-zinc-900 rounded-lg border border-zinc-800">
                <p className="text-zinc-500 text-xs mb-1">Plaka</p>
                <p className="text-amber-400 font-semibold">{companyInfo.plate}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* SEO Keywords Section */}
      <div className="bg-zinc-900/50 border-t border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <p className="text-zinc-600 text-xs text-center">
            {seoKeywords.join(' • ')}
          </p>
        </div>
      </div>

      {/* Copyright */}
      <div className="bg-zinc-900 border-t border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-zinc-500 text-sm text-center md:text-left">
              &copy; {new Date().getFullYear()} {companyInfo.name}. Tüm hakları saklıdır.
            </p>
            <button
              onClick={scrollToTop}
              className="flex items-center gap-2 text-zinc-500 hover:text-amber-400 transition-colors text-sm"
            >
              <ArrowUp className="w-4 h-4" />
              Yukarı Çık
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
